import React, { useState, useMemo } from 'react';
import './InspectionsTable.css';
import { useAuthContext } from '../../context/AuthContext';
import { INSPECTION_STATUS_LABELS } from '../../utils/constants';

const InspectionsTable = ({
    data = [],
    loading = false,
    error = null,
    onAddInspection,
    onRefresh,
    onRowClick,
    showSearch = true,
    title = "Tabla de Inspecciones",
    navigateToDetail
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const { currentUser } = useAuthContext();
    const itemsPerPage = 10;

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) {
            return data;
        }

        const searchLower = searchTerm.toLowerCase();
        return data.filter(inspection =>
            inspection.id?.toLowerCase().includes(searchLower) ||
            inspection.licensePlate?.toLowerCase().includes(searchLower)
        );
    }, [data, searchTerm]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSelectChange = (e) => {
        const selectedPage = parseInt(e.target.value, 10);
        if (selectedPage >= 1 && selectedPage <= totalPages) {
            setCurrentPage(selectedPage);
        }
    };

    const handleRowClick = (inspection) => {
        if (navigateToDetail) {
            navigateToDetail(inspection.id);
        } else if (onRowClick) {
            onRowClick(inspection);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleRefresh = () => {
        if (onRefresh) {
            const button = document.querySelector('.refresh-button');
            if (button) {
                button.classList.add('loading');
            }
            onRefresh();
            setTimeout(() => {
                if (button) {
                    button.classList.remove('loading');
                }
            }, 1000);
        }
    };

    const enableAddInspectionButton = () => {
        if (currentUser.role === 'Inspector') {
            return false;
        }
        return true;
    }

    const formatDate = (date) => {
        const d = new Date(date);
        const options = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: 'America/Bogota'
        };
        // toLocaleString returns "DD/MM/AAAA HH:MM"
        const newDate = d.toLocaleString('es-ES', options).replace(',', '');
        return newDate;
    }

    if (loading) {
        return (
            <div className="inspections-table-component">
                <div className="table-loading">
                    <div className="loading-spinner"></div>
                    <p>Cargando inspecciones...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="inspections-table-component">
                <div className="table-error">
                    <p>Error: {error}</p>
                    <button onClick={() => window.location.reload()} className="btn btn-primary">
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="inspections-table-component">
            {/* Table Section */}
            <div className="table-section">
                <div className="table-header">
                    <div className="table-header-left">
                        {showSearch && (
                            <div className="search-container">
                                <input
                                    type="text"
                                    placeholder="Buscar por placa o N° de inspección..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    className="table-search-input"
                                />
                            </div>
                        )}
                    </div>
                    <div className="table-header-center">
                        <button
                            className="refresh-button"
                            onClick={handleRefresh}
                            disabled={loading}
                            title="Actualizar datos"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 4v6h-6" />
                                <path d="M1 20v-6h6" />
                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                            </svg>
                        </button>
                    </div>
                    <div className="table-header-right">
                        {enableAddInspectionButton() && (
                            <button
                                className="add-inspection-button"
                                onClick={onAddInspection}
                            >
                                Añadir Inspección
                            </button>
                        )}
                    </div>
                </div>

                <div className="table-container">
                    <table className="inspections-table">
                        <thead>
                            <tr>
                                <th>N° de Inspección</th>
                                <th>Fecha de Creación</th>
                                <th>Creado Por</th>
                                <th>Empresa</th>
                                <th>Placa</th>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentData.map((inspection, index) => (
                                <tr
                                    key={inspection.id || index}
                                    onClick={() => handleRowClick(inspection)}
                                    className={onRowClick ? 'clickable-row' : ''}
                                >
                                    <td>{inspection.id}</td>
                                    <td>{formatDate(inspection.creationDate)}</td>
                                    <td>{inspection.createdBy}</td>
                                    <td>{inspection.insuranceCompany}</td>
                                    <td>{inspection.licensePlate}</td>
                                    <td className={`status-badge ${inspection.status}`}>{INSPECTION_STATUS_LABELS[inspection.status]}</td>
                                </tr>
                            ))}
                            {currentData.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="no-data">
                                        No hay datos disponibles
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination">
                        <button
                            className="pagination-button pagination-button--prev"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Anterior
                        </button>

                        <div className="pagination-info">
                            <span className="pagination-label">Ir a página:</span>
                            <select
                                className="pagination-select"
                                value={currentPage}
                                onChange={handlePageSelectChange}
                            >
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <option key={page} value={page}>
                                        {page}
                                    </option>
                                ))}
                            </select>
                            <span className="pagination-total">de {totalPages}</span>
                        </div>

                        <button
                            className="pagination-button pagination-button--next"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            Siguiente
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InspectionsTable; 